// Tinsmith Web — entry point.
//
// Boots React 18, the TanStack Query client, and the TanStack Router. The
// service-worker registration is handled by vite-plugin-pwa via virtual module
// import and runs in production builds only.

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import * as Sentry from '@sentry/react';
import { router } from './router.js';
import { createQueryClient } from './lib/query.js';
import { startOutboxProcessor } from './lib/outbox-processor.js';
import { startSessionKeeper } from './lib/session-keeper.js';
import { bootstrapThemeFromStorage } from './lib/theme.js';
import { initSentry } from './lib/sentry.js';
import './index.css';

// Sentry initializes BEFORE React mounts so its ErrorBoundary catches
// render-time throws on the very first paint. No-op when VITE_SENTRY_DSN
// is unset (dev). Returns false in that case but the rest of the app
// still works.
initSentry();

// Apply the user's last-known theme / density before React paints so they
// don't see a light-mode flash while /me is in flight.
bootstrapThemeFromStorage();

const queryClient = createQueryClient();

// Drain queued sales-order submissions in the background. Hooks window 'online'
// + a 30s interval so a tech who comes back into signal gets their pending
// quotes synced without any explicit action.
startOutboxProcessor();

// Restore the Cognito session at boot and silently refresh tokens before
// they expire (Cognito ID tokens die hourly; the refresh token lasts 30d).
// No-op under the dev IDP.
startSessionKeeper();

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');

createRoot(rootEl).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={SentryFallback} showDialog={false}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);

/**
 * Top-level error fallback. Mirrors the Feedback component's tone but
 * deliberately doesn't import it — when the tree below is broken we
 * can't trust anything that touches the design system.
 */
function SentryFallback({ resetError }: { resetError: () => void }) {
  return (
    <div style={{ padding: '2rem', fontFamily: 'system-ui, sans-serif', maxWidth: '36rem', margin: '4rem auto' }}>
      <h1 style={{ fontSize: '1.25rem', marginBottom: '0.75rem' }}>Something went wrong.</h1>
      <p style={{ color: '#64748B', marginBottom: '1rem' }}>
        The error has been reported. You can try again or reload the page.
      </p>
      <button
        type="button"
        onClick={resetError}
        style={{
          padding: '0.5rem 1rem',
          borderRadius: '0.5rem',
          background: '#185FA5',
          color: 'white',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        Try again
      </button>
    </div>
  );
}
